import { Print } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAtom } from "jotai";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useNavigate } from "react-router";
import { SubmitButton } from "../../../../../components/elements";
import PATHS from "../../../../../components/navigation/_paths";
import { useGlobalUserContext } from "../../../../../hooks/useGlobalUserContext";
import { useAlert } from "../../../../../lib/alert";
import { publicPageAtom } from "../../../../organization/state/publicPageAtom";
import { useProcessAnimationContext } from "../../../../process/components/AnimationContext";
import ConfirmationModal from "../../../../process/components/confirmation-modal/ConfirmationModal";
import { useProcessExecutionContext } from "../../../../process/hooks";
import {
  taskEditingAtom,
  useTaskExecutionEditingState,
} from "../../../hooks/useTaskExecutionEditingState";
import { useValidation } from "../../../hooks/useValidation";
import {
  CopilotAutofillElement,
  FormElementType,
  TaskExecutionState,
  TaskForm,
  TaskFormExecution,
  TaskViews,
} from "../../../types";
import { CopilotAutofillFormComponent } from "../../form-builder/copilot-autofill/CopilotAutofillFormComponent";
import { DEFAULT_RENDER } from "../TaskBody";
import executeFormTask from "./executeFormTask";
import getCommonFormExecutionComponent from "./FormCommonExecutionComponents";
import { FormExecutionComponentProps } from "./FormExecutionComponent";

const LinearFormExecutionComponent = ({
  children = DEFAULT_RENDER,
  onExecute,
  hideCompleteButton,
  hideEditButton,
  showPrint = false,
  ...props
}: FormExecutionComponentProps) => {
  const navigate = useNavigate();
  const { error } = useAlert();
  const anim = useProcessAnimationContext();
  const { executeTask } = useProcessExecutionContext();
  const [editing, setEditing] = useAtom(taskEditingAtom);
  const [publicPage] = useAtom(publicPageAtom);

  const { validationFailures } = useValidation();

  const { editable } = useTaskExecutionEditingState(
    props.taskExecution,
    props.task,
  );
  const [openConfirmationModal, setOpenConfirmationModal] =
    useState<boolean>(false);

  const { user } = useGlobalUserContext();

  // instead of doing this check multiple times we can do it once here
  const isEditableAndEnabled = !props.isDisabled && (editable || publicPage);

  // cast input data so TS compiler doesn't bark
  const propsTask = props.task as TaskForm;
  const propsExecution = props.taskExecution as TaskFormExecution | undefined;
  const validElements = propsExecution?.data?.wizardData?.validationIndexes;

  useEffect(() => {
    if (editing === undefined) {
      setEditing(propsExecution?.state !== TaskExecutionState.Completed);
    }
  }, [editing, propsExecution?.state, setEditing]);

  const [autofillRefresh, setAutofillRefresh] = useState(false);

  const trueElements = useMemo(() => {
    return props.elements ?? propsTask.data?.form?.elements ?? [];
  }, [props.elements, propsTask.data?.form?.elements]);

  const nested = props.elements && props.elements.length > 0;
  const padDescription =
    props.view === TaskViews.CONDENSED &&
    !!props.task.description &&
    props.task.description.length > 0;

  const [disabled, setDisabled] = useState<boolean>(
    !(editing ?? !props.isDisabled),
  );

  useEffect(() => {
    setDisabled(!(editing ?? !props.isDisabled));
  }, [editing, props.isDisabled, props.taskExecution]);

  const autofillElements = useCallback(
    (autofill: Record<string, unknown>) => {
      if (!Array.isArray(trueElements)) return;
      trueElements.forEach((element) => {
        const updates = autofill[element.id];
        if (updates && typeof updates === "object") {
          Object.assign(element, updates);
        }
      });
      setAutofillRefresh((prev) => !prev);
    },
    [trueElements],
  );

  const contents = useMemo(() => {
    if (autofillRefresh) {
      // autofillRefresh is used to rerender after autofillElements is called
    }
    return trueElements
      .map((element, index) => {
        const formElementExecution = (
          props.executionElements ??
          propsExecution?.data?.form?.elements ??
          []
        ).find(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (e: any) => e.id == element.id,
        );

        const ignoreTypes = [
          FormElementType.Summary,
          FormElementType.Typography,
          FormElementType.ButtonGroup,
        ];

        // If this is a wizard specific element, ignore it
        if (!element.type || ignoreTypes.includes(element.type)) return;

        const elem = getCommonFormExecutionComponent({
          element: element,
          trueElements: trueElements,
          index: index,
          state: propsExecution?.state,
          formElementExecution: formElementExecution,
          disabled: disabled,
          nested: nested,
          task: props.task,
          taskExecution: props.taskExecution,
          validElements: validElements,
        });

        if (!elem)
          switch (element.type) {
            case FormElementType.CopilotAutofill:
              return (
                <CopilotAutofillFormComponent
                  {...(element as CopilotAutofillElement)}
                  execution={formElementExecution}
                  taskExecution={props.taskExecution}
                  onAutofillComplete={autofillElements}
                />
              );
          }

        if (!elem)
          console.warn(
            `Unable to map element of type ${element.type} to a corresponding JSX element in the Linear renderer.`,
          );

        return elem;
      })
      .filter((e) => e)
      .map((component) => {
        return (
          <ErrorBoundary
            key={component?.key}
            fallback={
              <Alert severity="warning" sx={{ mt: 1 }}>
                {`Something went wrong displaying ${
                  component?.props?.label ?? "this element"
                }`}
              </Alert>
            }
          >
            {component}
          </ErrorBoundary>
        );
      });
  }, [
    autofillElements,
    autofillRefresh,
    disabled,
    nested,
    props.executionElements,
    props.task,
    props.taskExecution,
    propsExecution?.data?.form?.elements,
    propsExecution?.state,
    trueElements,
    validElements,
  ]);

  const submitExecution = useCallback(
    async (saveOnly?: boolean) => {
      try {
        const execution = {
          ...propsExecution,
          task_id: propsTask.id,
          state: TaskExecutionState.Completed,
          ...executeFormTask(propsTask, saveOnly, props.taskExecution),
        };

        if (onExecute) {
          await onExecute(execution, !saveOnly).then(() => {
            anim?.attemptToExecuteAnimation();
          });
        } else {
          await executeTask(execution, !saveOnly).then(() => {
            anim?.attemptToExecuteAnimation();
          });
        }

        setEditing(false);
        // Error should be of type any so that it can be used directly in the error
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        error(err.toString());
      }
    },
    [
      anim,
      error,
      executeTask,
      onExecute,
      props.taskExecution,
      propsExecution,
      propsTask,
      setEditing,
    ],
  );

  const SubmitExecutionButton = useCallback(
    ({
      tooltipTitle,
      buttonText,
      buttonVariant = "contained",
      saveOnly,
    }: {
      tooltipTitle?: string;
      buttonText: string;
      buttonVariant?: "outlined" | "contained";
      saveOnly?: boolean;
    }) => {
      return (
        <Tooltip arrow placement="bottom-start" title={tooltipTitle}>
          <Box>
            <SubmitButton
              variant={buttonVariant}
              disabled={Object.keys(validationFailures).length > 0}
              onSubmit={async () => {
                !saveOnly && props.task?.data?.confirmation
                  ? setOpenConfirmationModal(true)
                  : submitExecution(saveOnly);
              }}
            >
              {buttonText}
            </SubmitButton>
          </Box>
        </Tooltip>
      );
    },
    [props.task, submitExecution, validationFailures],
  );

  const EditButtons = useCallback(() => {
    if (hideEditButton) return <></>;
    return (
      <>
        {!editing && (
          <Button
            variant="outlined"
            onClick={() => {
              setEditing(true);
            }}
          >
            Edit
          </Button>
        )}
        {editing &&
          props.taskExecution?.state == TaskExecutionState.Completed && (
            <>
              <Button variant="outlined" onClick={() => setEditing(false)}>
                Cancel
              </Button>
              <SubmitExecutionButton
                tooltipTitle={"Update form submission"}
                buttonText={"Update"}
                buttonVariant={"contained"}
                saveOnly
              />
            </>
          )}
      </>
    );
  }, [
    hideEditButton,
    editing,
    props.taskExecution?.state,
    SubmitExecutionButton,
    setEditing,
  ]);

  const actions = (
    <Grid
      container
      gap={1}
      mt={1}
      width={"100%"}
      justifyContent={"flex-end"}
      alignItems={"center"}
    >
      {props.taskExecution?.state == TaskExecutionState.Completed &&
        props.taskExecution?.completed_at &&
        !nested && (
          <Typography variant="body1">
            {`Completed: ${new Date(
              props.taskExecution?.completed_at,
            ).toLocaleString()}`}
          </Typography>
        )}
      <Box flex={1}></Box>
      {(editable || publicPage) && <EditButtons />}
      {showPrint && (
        <IconButton
          onClick={() =>
            navigate(
              PATHS.EXECUTION_TASK_FORM.linkTo(
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                (props.processExecution?.process_id ?? props.task.process_id)!,
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                (props.processExecution?.id ??
                  props.taskExecution?.process_execution_id)!,
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                props.task.id!,
                true,
              ),
            )
          }
        >
          <Print />
        </IconButton>
      )}
      {props.taskExecution?.state !== TaskExecutionState.Completed &&
        isEditableAndEnabled &&
        !hideCompleteButton && (
          <>
            {user.id && editing && (
              <SubmitExecutionButton
                tooltipTitle={"Save your progress and complete the form later."}
                buttonText={"Save Draft"}
                buttonVariant={"outlined"}
                saveOnly
              />
            )}
            <SubmitExecutionButton
              tooltipTitle={
                "Submitting this form finalizes your entries, and modifications are no longer possible. You will still be able to view your entries."
              }
              buttonText={"Complete"}
            />
          </>
        )}
    </Grid>
  );

  const content = (
    <Grid
      container
      rowSpacing={props.isDisabled || !editing ? 1 : 2}
      direction="column"
      alignContent="stretch"
      pt={padDescription ? 2 : 0}
      pl={nested ? 3 : 0}
      width={"100%"}
      sx={{
        "@media print": {
          paddingBottom: "64px", // Make all content visible for printing
        },
      }}
    >
      {contents}
      <ConfirmationModal
        executeModal={submitExecution}
        openModal={openConfirmationModal}
        confirmationData={props.task?.data?.confirmation}
        handleCloseModal={() => {
          setOpenConfirmationModal(false);
        }}
      />
    </Grid>
  );

  return children({ actions, content });
};

export default LinearFormExecutionComponent;
